import type { Sale } from '../../types'
import saleService from '../../services/saleService'
import {
  getTermSaleAmountReceived,
  getTermSaleProfitBreakdown,
  type TermProfitBreakdown,
} from '../../utils/saleRealProfit'
import { resolveSaleInstallments } from '../../utils/saleTermInstallments'

const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  const s = String(v).trim()
  if (s === '') return 0
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0
  return parseFloat(s) || 0
}

export type TermSaleReportItem = {
  sale: Sale
  vehicleLabel: string
  buyerLabel: string
  buyerPhone: string
  breakdown: TermProfitBreakdown
  amountReceived: number
  amountPending: number
  hasPersistedInstallments: boolean
}

const getVehicleLabel = (sale: Sale): string => {
  const v = sale.vehicle
  if (!v) return sale.vehicle_id ? `Veículo #${sale.vehicle_id}` : '—'
  const plate = v.identification
    ? v.identification
    : v.no_plate
      ? 'Sem placa'
      : v.plate || '—'
  return `${v.brand ?? ''} ${v.model ?? ''}`.trim() + ` (${plate})`
}

export async function buildTermSalesReportItems(sales: Sale[]): Promise<TermSaleReportItem[]> {
  const termSales = sales.filter((s) => s.payment_method === 'term')
  const enriched = await saleService.enrichAllTermSalesWithInstallments(termSales)

  const items = enriched.map((sale) => {
    const persisted = Boolean(sale.installments?.length)
    const installments = resolveSaleInstallments(sale)
    const saleWithInstallments = { ...sale, installments }
    const breakdown = getTermSaleProfitBreakdown(saleWithInstallments, installments)!
    const salePrice = toNum(sale.sale_price)
    const amountReceived = getTermSaleAmountReceived(saleWithInstallments)

    return {
      sale: saleWithInstallments,
      vehicleLabel: getVehicleLabel(sale),
      buyerLabel: sale.buyer_name || sale.customer_name || '—',
      buyerPhone: sale.buyer_phone || sale.customer_contact || '—',
      breakdown,
      amountReceived,
      amountPending: Math.max(0, salePrice - amountReceived),
      hasPersistedInstallments: persisted,
    }
  })

  return items.sort((a, b) => String(b.sale.sale_date).localeCompare(String(a.sale.sale_date)))
}
